import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Paper,
  Avatar,
  Chip,
  IconButton,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Drawer,
  ListItemIcon,
  ListItemButton,
  Collapse,
} from "@mui/material";
import {
  TrendingUp,
  People,
  Assignment,
  CheckCircle,
  Warning,
  Description,
  Settings,
  MoreVert,
  Add,
  Refresh,
  Dashboard as DashboardIcon,
  ExpandLess,
  ExpandMore,
  InsertDriveFile,
  Business,
  AccountBalance,
  Report,
  Analytics,
  Group,
  Inventory,
  Receipt,
  Schedule,
  Assessment,
  Email,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import Header from "./Header";
import SampleDataGrid from "./SampleDataGrid";
import AppointmentSchedule from "./AppointmentSchedule";
import EmailSetupPage from "./EmailSetupPage";
import EmailLogsPage from "./EmailLogsPage";
import EmailComposePage from "./EmailComposePage";
import UserManagementPage from "./UserManagementPage";
import ParamExUrTemplatePage from "./ParamExUrTemplatePage";

// Constants
const DRAWER_WIDTH = 260;

// Styled components for enhanced visual appeal
const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[8],
  },
}));

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: "left",
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: "white",
  borderRadius: theme.spacing(2),
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
    transition: "left 0.5s",
  },
  "&:hover::before": {
    left: "100%",
  },
}));

const WelcomeSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.light} 100%)`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  color: "white",
  marginBottom: theme.spacing(1.5),
  position: "relative",
  overflow: "hidden",
}));

interface DashboardStats {
  totalUsers: number;
  activeProjects: number;
  completedTasks: number;
  revenue: string;
}

interface RecentActivity {
  id: number;
  title: string;
  description: string;
  time: string;
  type: "success" | "warning" | "info" | "error";
  icon: React.ReactNode;
}

interface QuickAction {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: "primary" | "secondary" | "success" | "warning";
  action: () => void;
}

interface TreeMenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  children?: TreeMenuItem[];
  path?: string;
  expanded?: boolean;
}

// API configuration (same defaults as other pages)
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";
const API_ENDPOINTS = {
  dashboardStats:
    process.env.REACT_APP_DASHBOARD_STATS_ENDPOINT || "/Dashboard/Stats",
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<string>("dashboard");
  const [expandedItems, setExpandedItems] = useState<string[]>([
    "management",
    "reports",
  ]);
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeProjects: 0,
    completedTasks: 156,
    revenue: "$47,500",
  });

  // Tree menu data structure
  const treeMenuData: TreeMenuItem[] = [
    {
      id: "dashboard",
      label: t("dashboard.menu.dashboard"),
      icon: <DashboardIcon />,
      path: "/dashboard",
    },
    {
      id: "management",
      label: t("dashboard.menu.management"),
      icon: <Business />,
      children: [
        {
          id: "users",
          label: t("dashboard.menu.users"),
          icon: <Group />,
          path: "/users",
        },
        {
          id: "departments",
          label: t("dashboard.menu.departments"),
          icon: <AccountBalance />,
          path: "/departments",
        },
        {
          id: "projects",
          label: t("dashboard.menu.projects"),
          icon: <Assignment />,
          path: "/projects",
        },
      ],
    },
    {
      id: "documents",
      label: t("dashboard.menu.documents"),
      icon: <Description />,
      children: [
        {
          id: "patients",
          label: t("dashboard.menu.patients"),
          icon: <InsertDriveFile />,
          path: "/patients",
        },
        {
          id: "appointment",
          label: t("dashboard.menu.appointments"),
          icon: <Schedule />,
          path: "/appointments",
        },
        {
          id: "inventory",
          label: t("dashboard.menu.inventory"),
          icon: <Inventory />,
          path: "/inventory",
        },
      ],
    },
    {
      id: "reports",
      label: t("dashboard.menu.reports"),
      icon: <Report />,
      children: [
        {
          id: "analytics",
          label: t("dashboard.menu.analytics"),
          icon: <Analytics />,
          path: "/analytics",
        },
        {
          id: "performance",
          label: t("dashboard.menu.performance"),
          icon: <Assessment />,
          path: "/performance",
        },
        {
          id: "schedule",
          label: t("dashboard.menu.schedule"),
          icon: <Schedule />,
          path: "/schedule",
        },
      ],
    },
    {
      id: "settings",
      label: t("dashboard.menu.settings"),
      icon: <Settings />,
      path: "/settings",
      children: [
        {
          id: "emailsetup",
          label: t("dashboard.menu.emailsetup"),
          icon: <Email />,
          path: "/emailsetup",
          children: [
            {
              id: "emailsetup",
              label: t("dashboard.menu.emailsetup"),
              icon: <Email />,
              path: "/emailsetup",
            },
            {
              id: "emaillogs",
              label: t("dashboard.menu.emaillogs"),
              icon: <Assessment />,
              path: "/emaillogs",
            },
            {
              id: "emailcompose",
              label: t("dashboard.menu.emailcompose"),
              icon: <Schedule />,
              path: "/emailcompose",
            },
          ],
        },
        {
          id: "performance",
          label: t("dashboard.menu.performance"),
          icon: <Assessment />,
          path: "/performance",
        },
        {
          id: "schedule",
          label: t("dashboard.menu.schedule"),
          icon: <Schedule />,
          path: "/schedule",
        },
        {
          id: "paramexurtemplate",
          label: t("dashboard.menu.paramExUrTemplate"),
          icon: <Receipt />,
          path: "/settings/param-ex-ur-template",
        },
      ],
    },
  ];

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();
    const traceId = `dash-${startedAt}-${Math.random().toString(16).slice(2, 8)}`;

    const loadDashboardData = async () => {
      console.log(`[Dashboard][${traceId}] loadDashboardData: start`);
      console.log(`[Dashboard][${traceId}] API_BASE_URL: ${API_BASE_URL}`);
      console.log(
        `[Dashboard][${traceId}] dashboardStats endpoint: ${API_ENDPOINTS.dashboardStats}`,
      );

      // Load user data from localStorage
      const userData = localStorage.getItem("user");
      if (userData) {
        try {
          console.log(
            `[Dashboard][${traceId}] Found user in localStorage (bytes=${userData.length})`,
          );
          if (!cancelled) setUser(JSON.parse(userData));
          console.log(`[Dashboard][${traceId}] User state set`);
        } catch (error) {
          console.error("[Dashboard] Error parsing user data:", error);
        }
      } else {
        console.log(`[Dashboard][${traceId}] No user found in localStorage`);
      }

      // Load stats from API (patients + consultations)
      try {
        const authToken = localStorage.getItem("authToken");
        console.log(
          `[Dashboard][${traceId}] Preparing stats request (hasToken=${!!authToken}, hasApiKey=${!!process.env.REACT_APP_API_KEY})`,
        );

        const config = {
          timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || "10000", 10),
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-API-Key": process.env.REACT_APP_API_KEY,
          } as Record<string, string | undefined>,
        };

        // Single API call must return both totals.
        // Expected shape (example): { totalPatients: number, totalConsultations: number }
        const statsUrl = `${API_BASE_URL}${API_ENDPOINTS.dashboardStats}`;
        console.log(`[Dashboard][${traceId}] GET ${statsUrl}`);
        const statsResponse = await axios.get(statsUrl, config);
        console.log(
          `[Dashboard][${traceId}] Stats response received `,
          statsResponse.data,
        );

        const statsData = statsResponse.data;
        const statsKeys =
          statsData && typeof statsData === "object"
            ? Object.keys(statsData)
            : [];
        console.log(
          `[Dashboard][${traceId}] Stats response keys: ${statsKeys.join(", ") || "(none)"}`,
        );

        const totalPatients =
          (typeof statsData?.totalPatients === "number"
            ? statsData.totalPatients
            : typeof statsData?.persoaneCount === "number"
              ? statsData.persoaneCount
              : typeof statsData?.patientsCount === "number"
                ? statsData.patientsCount
                : typeof statsData?.patients === "number"
                  ? statsData.patients
                  : 0) || 0;

        const totalConsultations =
          (typeof statsData?.totalConsultations === "number"
            ? statsData.totalConsultations
            : typeof statsData?.consultatiiCount === "number"
              ? statsData.consultatiiCount
              : typeof statsData?.consultationsCount === "number"
                ? statsData.consultationsCount
                : typeof statsData?.consultations === "number"
                  ? statsData.consultations
                  : 0) || 0;

        console.log(
          `[Dashboard][${traceId}] Parsed totals: totalPatients=${totalPatients}, totalConsultations=${totalConsultations}`,
        );

        if (!cancelled) {
          setStats((prev) => ({
            ...prev,
            totalUsers: totalPatients,
            activeProjects: totalConsultations,
          }));
          console.log(`[Dashboard][${traceId}] Stats state updated`, statsData);
        } else {
          console.log(
            `[Dashboard][${traceId}] Skipped stats update (component unmounted)`,
          );
        }
      } catch (error) {
        console.warn("[Dashboard] Failed to load stats from API:", error);
      } finally {
        // Keep the existing 500ms minimum for smooth transitions.
        const elapsed = Date.now() - startedAt;
        const remaining = Math.max(0, 500 - elapsed);
        console.log(
          `[Dashboard][${traceId}] loadDashboardData: elapsed=${elapsed}ms, remainingDelay=${remaining}ms`,
        );
        setTimeout(() => {
          if (!cancelled) setLoading(false);
        }, remaining);
      }
    };

    loadDashboardData();

    return () => {
      cancelled = true;
      console.log(`[Dashboard][${traceId}] cleanup: cancelled=true`);
    };
  }, []);

  const handleItemClick = (itemId: string, path?: string) => {
    setSelectedItem(itemId);
    if (path) {
      console.log(`[Dashboard] Navigate to: ${path}`);
      // Here you can add navigation logic
    }
  };

  const renderMainContent = () => {
    switch (selectedItem) {
      case "users":
        return <UserManagementPage />;
      case "patients":
        return <SampleDataGrid />;
      case "appointment":
        return <AppointmentSchedule />;
      case "emailsetup":
        return <EmailSetupPage />;
      case "emaillogs":
        return <EmailLogsPage />;
      case "emailcompose":
        return <EmailComposePage />;
      case "paramexurtemplate":
        return <ParamExUrTemplatePage />;
      /*case 'projects':
      return <ProjectsPage />;
    case 'departments':
      return <DepartmentsPage />;
    case 'invoices':
      return <InvoicesPage />;
    case 'inventory':
      return <InventoryPage />;
    case 'analytics':
      return <AnalyticsPage />;
    case 'performance':
      return <PerformancePage />;
    case 'schedule':
      return <SchedulePage />;
    case "settings":
        return <LanguageSelector />;
    case 'dashboard':*/
      default:
        return renderDashboardContent();
    }
  };

  // Extract your current dashboard content into a separate function
  const renderDashboardContent = () => (
    <>
      <Box sx={{ py: 1, px: 1, width: "100%" }}>
        {/* Welcome Section */}
        <WelcomeSection>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "flex-start",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 250, textAlign: "left" }}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{ fontWeight: "bold", textAlign: "left" }}
              >
                {t("dashboard.welcome", {
                  name: user?.name || user?.username || t("common.user"),
                })}{" "}
                👋
              </Typography>
              <Typography
                variant="body1"
                sx={{ opacity: 0.9, mb: 2, textAlign: "left" }}
              >
                {t("dashboard.overview")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  justifyContent: "flex-start",
                }}
              >
                <Chip
                  label={t("dashboard.department", {
                    dept: user?.department || "General",
                  })}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    color: "white",
                  }}
                />
                <Chip
                  label={t("dashboard.roles", {
                    count: user?.roles?.length || 0,
                  })}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    color: "white",
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ textAlign: "left", alignSelf: "flex-start" }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: "rgba(255,255,255,0.2)",
                  fontSize: "1.5rem",
                  margin: "0",
                }}
              >
                {user?.displayName?.[0] || user?.username?.[0] || "U"}
              </Avatar>
            </Box>
          </Box>
        </WelcomeSection>

        {/* Statistics Cards */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 2,
            justifyContent: "flex-start",
          }}
        >
          <Box sx={{ flex: "1 1 200px" }}>
            <StatCard>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  mb: 1,
                }}
              >
                <People sx={{ fontSize: 32, opacity: 0.8 }} />
                <Box sx={{ textAlign: "left" }}>
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {stats.totalUsers.toLocaleString()}
                  </Typography>
                  <Typography variant="subtitle1">
                    {t("dashboard.stats.totalUsers")}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {t("dashboard.growth.usersGrowth")}
                  </Typography>
                </Box>
              </Box>
            </StatCard>
          </Box>
          <Box sx={{ flex: "1 1 200px" }}>
            <StatCard
              sx={{
                background: "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  mb: 1,
                }}
              >
                <Assignment sx={{ fontSize: 32, opacity: 0.8 }} />
                <Box sx={{ textAlign: "left" }}>
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {stats.activeProjects.toLocaleString()}
                  </Typography>
                  <Typography variant="subtitle1">
                    {t("dashboard.stats.activeProjects")}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {t("dashboard.growth.projectsNew")}
                  </Typography>
                </Box>
              </Box>
            </StatCard>
          </Box>
          <Box sx={{ flex: "1 1 200px" }}>
            <StatCard
              sx={{
                background: "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  mb: 1,
                }}
              >
                <CheckCircle sx={{ fontSize: 32, opacity: 0.8 }} />
                <Box sx={{ textAlign: "left" }}>
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {stats.completedTasks}
                  </Typography>
                  <Typography variant="subtitle1">
                    {t("dashboard.stats.completedTasks")}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {t("dashboard.growth.tasksThisMonth")}
                  </Typography>
                </Box>
              </Box>
            </StatCard>
          </Box>
          <Box sx={{ flex: "1 1 200px" }}>
            <StatCard
              sx={{
                background: "linear-gradient(135deg, #9C27B0 0%, #6A1B9A 100%)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  mb: 1,
                }}
              >
                <TrendingUp sx={{ fontSize: 32, opacity: 0.8 }} />
                <Box sx={{ textAlign: "left" }}>
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {stats.revenue}
                  </Typography>
                  <Typography variant="subtitle1">
                    {t("dashboard.stats.revenue")}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {t("dashboard.growth.revenueGrowth")}
                  </Typography>
                </Box>
              </Box>
            </StatCard>
          </Box>
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            justifyContent: "flex-start",
          }}
        >
          {/* Quick Actions */}
          <Box sx={{ flex: "1 1 400px" }}>
            <StyledCard>
              <CardContent sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{ fontWeight: "bold" }}
                  >
                    {t("dashboard.quickActions.title")}
                  </Typography>
                  <IconButton size="small">
                    <MoreVert />
                  </IconButton>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                  {quickActions.map((action) => (
                    <Box key={action.id} sx={{ flex: "1 1 180px" }}>
                      <Paper
                        sx={{
                          p: 1.5,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          border: "1px solid",
                          borderColor: "divider",
                          "&:hover": {
                            borderColor: `${action.color}.main`,
                            transform: "translateY(-2px)",
                            boxShadow: 3,
                          },
                        }}
                        onClick={action.action}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: `${action.color}.main`,
                              width: 32,
                              height: 32,
                              mr: 1.5,
                            }}
                          >
                            {action.icon}
                          </Avatar>
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: "bold" }}
                            >
                              {action.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {action.description}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </StyledCard>
          </Box>

          {/* Recent Activity */}
          <Box sx={{ flex: "1 1 400px" }}>
            <StyledCard>
              <CardContent sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{ fontWeight: "bold" }}
                  >
                    {t("dashboard.activity.title")}
                  </Typography>
                  <IconButton size="small">
                    <Refresh />
                  </IconButton>
                </Box>
                <List sx={{ p: 0 }}>
                  {recentActivities.slice(0, 3).map((activity, index) => (
                    <React.Fragment key={activity.id}>
                      <ListItem alignItems="flex-start" sx={{ px: 0, py: 1 }}>
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: `${getActivityColor(
                                activity.type,
                              )}.main`,
                              width: 32,
                              height: 32,
                            }}
                          >
                            {activity.icon}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: "medium" }}
                            >
                              {activity.title}
                            </Typography>
                          }
                          secondary={
                            <>
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.primary"
                              >
                                {activity.description}
                              </Typography>
                              <br />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {activity.time}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                      {index < 2 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button size="small" color="primary">
                  {t("dashboard.activity.viewAll")}
                </Button>
              </CardActions>
            </StyledCard>
          </Box>
        </Box>

        {/* Performance Chart Placeholder */}
        <Box sx={{ mt: 1.5 }}>
          <StyledCard>
            <CardContent sx={{ p: 2 }}>
              <Typography
                variant="h6"
                component="h2"
                sx={{ fontWeight: "bold", mb: 2 }}
              >
                {t("dashboard.performance.title")}
              </Typography>
              <Box
                sx={{
                  height: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  backgroundColor: "grey.100",
                  borderRadius: 2,
                  border: "2px dashed",
                  borderColor: "grey.300",
                  pl: 3,
                }}
              >
                <Box sx={{ textAlign: "left" }}>
                  <TrendingUp sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
                  <Typography variant="subtitle1" color="text.secondary">
                    {t("dashboard.performance.chartPlaceholder")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("dashboard.performance.chartDescription")}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Box>
      </Box>
    </>
  );

  const handleToggleExpand = (itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId],
    );
  };

  const renderTreeMenuItem = (item: TreeMenuItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const isSelected = selectedItem === item.id;
    const paddingLeft = 2 + level * 2;

    return (
      <React.Fragment key={item.id}>
        <ListItemButton
          sx={{
            pl: paddingLeft,
            pr: 1,
            borderLeft: isSelected ? "4px solid" : "4px solid transparent",
            borderColor: isSelected ? "primary.main" : "transparent",
            transition: "all 0.3s ease-in-out",
            "&:hover": {
              backgroundColor: isSelected ? "primary.dark" : "action.hover",
              transform: "translateX(2px)",
            },
            "&.Mui-selected": {
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              fontWeight: 600,
              boxShadow: "inset 0 0 10px rgba(0,0,0,0.1)",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
              "& .MuiListItemIcon-root": {
                color: "primary.contrastText",
                transform: "scale(1.1)",
              },
              "& .MuiListItemText-primary": {
                fontWeight: 700,
                fontSize: level === 0 ? "1rem" : "0.92rem",
              },
            },
          }}
          selected={isSelected}
          onClick={() => {
            if (hasChildren) {
              handleToggleExpand(item.id);
            } else {
              handleItemClick(item.id, item.path);
            }
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 36,
              color: isSelected ? "inherit" : "action.active",
              transition: "all 0.3s ease-in-out",
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: level === 0 ? "0.95rem" : "0.87rem",
              fontWeight: isSelected ? 700 : level === 0 ? "medium" : "regular",
            }}
          />
          {hasChildren && (
            <IconButton size="small" sx={{ color: "inherit" }}>
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </ListItemButton>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List disablePadding>
              {item.children!.map((child) =>
                renderTreeMenuItem(child, level + 1),
              )}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const recentActivities: RecentActivity[] = [
    {
      id: 1,
      title: t("dashboard.activity.newProject"),
      description: t("dashboard.activity.projectDesc"),
      time: t("dashboard.activity.time.hoursAgo", { count: 2 }),
      type: "success",
      icon: <CheckCircle />,
    },
    {
      id: 2,
      title: t("dashboard.activity.reportGenerated"),
      description: t("dashboard.activity.reportDesc"),
      time: t("dashboard.activity.time.hoursAgo", { count: 4 }),
      type: "info",
      icon: <Description />,
    },
    {
      id: 3,
      title: t("dashboard.activity.deadlineWarning"),
      description: t("dashboard.activity.deadlineDesc"),
      time: t("dashboard.activity.time.hoursAgo", { count: 6 }),
      type: "warning",
      icon: <Warning />,
    },
    {
      id: 4,
      title: t("dashboard.activity.newUser"),
      description: t("dashboard.activity.newUserDesc"),
      time: t("dashboard.activity.time.daysAgo", { count: 1 }),
      type: "info",
      icon: <People />,
    },
  ];

  const quickActions: QuickAction[] = [
    {
      id: 1,
      title: t("dashboard.quickActions.newProject"),
      description: t("dashboard.quickActions.newProjectDesc"),
      icon: <Add />,
      color: "primary",
      action: () => console.log("Create new project"),
    },
    {
      id: 2,
      title: t("dashboard.quickActions.reports"),
      description: t("dashboard.quickActions.reportsDesc"),
      icon: <Description />,
      color: "secondary",
      action: () => console.log("View reports"),
    },
    {
      id: 3,
      title: t("dashboard.quickActions.team"),
      description: t("dashboard.quickActions.teamDesc"),
      icon: <People />,
      color: "success",
      action: () => console.log("Manage team"),
    },
    {
      id: 4,
      title: t("dashboard.quickActions.settings"),
      description: t("dashboard.quickActions.settingsDesc"),
      icon: <Settings />,
      color: "warning",
      action: () => console.log("System settings"),
    },
  ];

  const getActivityColor = (type: string) => {
    switch (type) {
      case "success":
        return "success";
      case "warning":
        return "warning";
      case "error":
        return "error";
      default:
        return "info";
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <Box
          sx={{
            marginTop: "64px",
            paddingTop: 2,
            minHeight: "calc(100vh - 64px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 400 }}>
            <Typography variant="h6" align="center" gutterBottom>
              {t("common.loading")}
            </Typography>
            <LinearProgress sx={{ mt: 2 }} />
          </Box>
        </Box>
      </>
    );
  }

  return (
    <Box sx={{ display: "flex" }}>
      <Header />

      {/* Sidebar with Tree Menu */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            backgroundColor: "grey.50",
            borderRight: "1px solid",
            borderColor: "divider",
            top: "64px", // Account for header height
            height: "calc(100vh - 64px)",
          },
        }}
      >
        <Box sx={{ overflow: "auto", pt: 1.5 }}>
          <Typography
            variant="h6"
            sx={{
              px: 2,
              pb: 1.5,
              fontWeight: "bold",
              color: "text.secondary",
            }}
          >
            {t("dashboard.menu.navigation")}
          </Typography>
          <Divider />
          <List disablePadding sx={{ pt: 0.5 }}>
            {treeMenuData.map((item) => renderTreeMenuItem(item))}
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          marginTop: "64px",
          paddingTop: 1,
          minHeight: "calc(100vh - 64px)",
          backgroundColor: "grey.50",
          paddingLeft: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flexGrow: 1 }}>{renderMainContent()}</Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
